import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { AffiliateInviteRequest } from '@/api/generated';
import { useCurrentOperator } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { inviteEmailError, isSelfInvite, looksLikeEmail } from '@/tenant/inviteGuard';

export interface InviteAffiliateDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: (body: AffiliateInviteRequest) => Promise<void>;
}

const FIELDS = ['displayName', 'email'] as const;

export function InviteAffiliateDialog(props: InviteAffiliateDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <InviteAffiliateForm key={useFreshOnOpen(props.open)} {...props} />;
}

function InviteAffiliateForm({ open, onClose, onInvite }: InviteAffiliateDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const ownEmail = useCurrentOperator()?.email;
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { displayName: nameRef, email: emailRef }, submitting);

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const name = displayName.trim();
    const mail = email.trim();
    setError(null);
    const found: Partial<Record<(typeof FIELDS)[number], string>> = {};
    if (name.length === 0) found.displayName = t('validation.name');
    if (!looksLikeEmail(mail)) found.email = t('validation.email');
    else if (isSelfInvite(mail, ownEmail)) found.email = t('tenant.invite.self');
    if (Object.keys(found).length > 0) {
      fields.fail(found);
      return;
    }
    setSubmitting(true);
    fields.reset();
    try {
      await onInvite({ displayName: name, email: mail });
      onClose();
    } catch (err) {
      const action = describe(err);
      const emailError = inviteEmailError(err, t);
      if (emailError) {
        fields.fail({ email: emailError });
        return;
      }
      setError(action);
      fields.failMarked(
        mapFieldErrors(action.fieldKeys, { displayName: 'displayName', email: 'email' }),
        { displayName: t('affiliates.field.displayName'), email: t('affiliates.field.email') },
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('affiliates.inviteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {t('affiliates.inviteLead')}
          </Typography>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            required
            fullWidth
            margin="dense"
            label={t('affiliates.displayName')}
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              fields.clear('displayName');
            }}
            inputRef={nameRef}
            error={fields.errors.displayName !== undefined}
            helperText={fields.errors.displayName}
            disabled={submitting}
          />
          <TextField
            required
            fullWidth
            type="email"
            margin="dense"
            label={t('affiliates.email')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              fields.clear('email');
            }}
            inputRef={emailRef}
            error={fields.errors.email !== undefined}
            helperText={fields.errors.email}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('affiliates.inviteSend')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
